import { FormikContext, FormikProvider, useField, useFormik } from 'formik';
import moment from 'moment';
import { ReactNode, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import * as Yup from 'yup';

import { useFeature, useLocalize, useLocalizedValue } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Checkbox,
	Container,
	Editable,
	FormCard,
	GraphQLErrorMessage,
	Jumbo,
	Label,
	MultiSelect,
	NumericInput,
	Paragraph,
	Popover,
	QuillInput,
	Radio,
	SettingsSection,
	Skeleton,
	Table,
	Tabs,
	Tags,
	Title,
	Toolbar,
	tokens,
} from '@treams/treams-ui';

import GroupSelector from '../../../goals/group-selector/GroupSelector';

function Topics(props: { categories: graphql.EngagementCategory[] }) {
	const { localize } = useLocalize();

	const [{ value }, , { setValue }] = useField<graphql.EngagementCategory[]>('categories');
	const categories = value ?? props.categories;

	function option(category: graphql.EngagementCategory) {
		return {
			value: category.id,
			title: localize(category.title).text,
		};
	}

	return (
		<MultiSelect
			selectedOptions={categories.map(option)}
			options={props.categories.map(option)}
			onChange={(options) =>
				setValue(
					options.flatMap((categoryID) => {
						const category = props.categories.find(({ id }) => id === categoryID);
						return category ? [category] : [];
					}),
				)
			}
		/>
	);
}

function IntervalSelector() {
	const { t } = useTranslation();
	const [{ value }, , { setValue }] = useField<graphql.EngagementInterval | undefined>('interval');

	return (
		<Tags.Item.Select
			label={t('settings.engagement.interval.every.label')}
			options={[
				{
					title: t('settings.engagement.interval.every.weekly'),
					value: graphql.EngagementInterval.Weekly,
				},
				{
					title: t('settings.engagement.interval.every.biweekly'),
					value: graphql.EngagementInterval.Biweekly,
				},
				{
					title: t('settings.engagement.interval.every.monthly'),
					value: graphql.EngagementInterval.Monthly,
				},
				{
					title: t('settings.engagement.interval.every.bimonthly'),
					value: graphql.EngagementInterval.Bimonthly,
				},
				{
					title: t('settings.engagement.interval.every.quarterly'),
					value: graphql.EngagementInterval.Quarterly,
				},
				{
					title: t('settings.engagement.interval.every.halfYearly'),
					value: graphql.EngagementInterval.HalfYearly,
				},
				{
					title: t('settings.engagement.interval.every.yearly'),
					value: graphql.EngagementInterval.Yearly,
				},
			]}
			selectedValue={value ?? graphql.EngagementInterval.Weekly}
			onChange={(value) => setValue(value as graphql.EngagementInterval)}
		/>
	);
}

const IntervalMap = {
	[graphql.EngagementInterval.Weekly]: 'settings.engagement.result.description.weekly',
	[graphql.EngagementInterval.Biweekly]: 'settings.engagement.result.description.biweekly',
	[graphql.EngagementInterval.Monthly]: 'settings.engagement.result.description.monthly',
	[graphql.EngagementInterval.Bimonthly]: 'settings.engagement.result.description.bimonthly',
	[graphql.EngagementInterval.Quarterly]: 'settings.engagement.result.description.quarterly',
	[graphql.EngagementInterval.HalfYearly]: 'settings.engagement.result.description.halfYearly',
	[graphql.EngagementInterval.Yearly]: 'settings.engagement.result.description.yearly',
};

function EngagementSettingsSummary() {
	const { t } = useTranslation();

	const [{ value }] = useField<graphql.EngagementInterval | undefined>('interval');

	if (!value) {
		return null;
	}

	return <Paragraph>{t(IntervalMap[value])}</Paragraph>;
}

function MaxQuestions(props: { defaults: graphql.EngagementIntervalDefault }) {
	const { t } = useTranslation();

	const [{ value: maxQuestions }, , { setValue: setMaxQuestions }] = useField<string | undefined>(
		'maxQuestions',
	);

	const [{ value: maxOpenQuestions }, , { setValue: setMaxOpenQuestions }] = useField<
		string | undefined
	>('maxOpenQuestions');

	return (
		<>
			<Flex flexGrow={1} flexShrink={1} flexBasis={0} ml={4} alignItems="center">
				<Label mr={2}>{t('settings.engagement.configuration.maxOpenQuestions.label')}:</Label>
				<Box maxWidth={40}>
					<NumericInput
						placeholder={props.defaults.maxOpenQuestions.toFixed()}
						value={maxOpenQuestions}
						onChange={setMaxOpenQuestions}
						small
					/>
				</Box>
			</Flex>
			<Flex flexGrow={1} flexShrink={1} flexBasis={0} ml={2} alignItems="center">
				<Label mr={2}>{t('settings.engagement.configuration.maxQuestions.label')}:</Label>
				<Box maxWidth={40}>
					<NumericInput
						placeholder={props.defaults.maxQuestions.toFixed()}
						value={maxQuestions}
						onChange={setMaxQuestions}
						small
					/>
				</Box>
			</Flex>
		</>
	);
}

function UseAllQuestions() {
	const { t } = useTranslation(undefined, {
		keyPrefix: 'settings.engagement.configuration.useAllQuestions',
	});

	const [{ value: useAllQuestions }, , { setValue: setUseAllQuestions }] =
		useField<boolean>('useAllQuestions');

	return (
		<Box mt={3} maxWidth={600} ml={2}>
			<Radio.Option
				checked={!useAllQuestions}
				onChange={() => setUseAllQuestions(false)}
				title={t('disabled.title')}
				description={t('disabled.description')}
			/>
			<Radio.Option
				checked={useAllQuestions}
				onChange={() => setUseAllQuestions(true)}
				title={t('enabled.title')}
				description={t('enabled.description')}
			/>
		</Box>
	);
}

function Expiry() {
	const { t } = useTranslation();

	const [{ value: expiry }, , { setValue: setExpiry }] = useField<string | undefined>('expiry');

	return (
		<Flex maxWidth={400} mt={3}>
			<Box flexGrow={1} flexShrink={1} flexBasis={0} mr={2}>
				<Label mb={2}>{t('settings.engagement.configuration.expiry.label')}:</Label>
				<NumericInput value={expiry} onChange={setExpiry} />
			</Box>
		</Flex>
	);
}

const REMINDER_MIN_IN_DAYS = 1;
const REMINDER_DEFAULT_IN_DAYS = 1;

function Reminder() {
	const { t } = useTranslation();

	const [{ value: reminder }, { error }, { setValue: setReminder }] = useField<string | undefined>(
		'reminderDaysInAdvance',
	);
	const [reminderEnabled, setReminderEnabled] = useState<boolean>(reminder !== undefined);

	const enableInput = useCallback(() => {
		setReminderEnabled(true);
		setReminder(REMINDER_DEFAULT_IN_DAYS.toString());
	}, [setReminderEnabled, setReminder]);

	const disableInput = useCallback(() => {
		setReminderEnabled(false);
		setReminder(undefined);
	}, [setReminderEnabled, setReminder]);

	return (
		<>
			<Flex>
				<Checkbox
					checked={reminderEnabled}
					onClick={reminderEnabled ? disableInput : enableInput}
				/>
				<Label ml={2}>{t('settings.engagement.configuration.reminder.toggle')}</Label>
			</Flex>
			{reminderEnabled && (
				<Flex maxWidth={400} mt={3}>
					<Box flexGrow={1} flexShrink={1} flexBasis={0} mr={2}>
						<Label mb={2}>{t('settings.engagement.configuration.reminder.label')}:</Label>
						<NumericInput value={reminder} onChange={setReminder} error={error} />
					</Box>
				</Flex>
			)}
		</>
	);
}

function AnonymityThreshold(props: { hasSentSurvey?: boolean }) {
	const { t } = useTranslation();

	const [{ value }, , { setValue }] = useField<string>('anonymityThreshold');

	const allOptions = [
		{
			title: t('settings.engagement.anonymityThreshold.option.none'),
			value: '1',
		},
		{
			title: t('settings.engagement.anonymityThreshold.option.min2'),
			value: '2',
		},
		{
			title: t('settings.engagement.anonymityThreshold.option.min3'),
			value: '3',
		},
		{
			title: t('settings.engagement.anonymityThreshold.option.min4'),
			value: '4',
		},
		{
			title: t('settings.engagement.anonymityThreshold.option.min5'),
			value: '5',
		},
	];

	// If a survey has already been sent, only allow values >= 3
	const options = props.hasSentSurvey
		? allOptions.filter((option) => parseInt(option.value) >= 3)
		: allOptions;

	return (
		<Tags.Item.Select
			label={t('settings.engagement.anonymityThreshold.label')}
			options={options}
			selectedValue={value}
			onChange={(value) => value && setValue(value)}
		/>
	);
}

function EngagementSettingsStatusButton(props: {
	type?: 'primary' | 'secondary';
	title: string;
	loading: boolean;
	confirmTitle: string;
	extraInfo?: string;
	onConfirm: () => void;
}) {
	const { t } = useTranslation();

	const [visible, setVisible] = useState(false);

	return (
		<Popover.Confirm
			visible={visible}
			title={props.confirmTitle}
			confirmTitle={t('settings.engagement.setup.confirm.yes')}
			cancelTitle={t('settings.engagement.setup.confirm.no')}
			extraInfo={props.extraInfo}
			onCancel={() => setVisible(false)}
			onConfirm={() => {
				props.onConfirm();
				setVisible(false);
			}}
		>
			<Button
				type={props.type}
				loading={props.loading}
				title={props.title}
				onClick={() => setVisible(true)}
			/>
		</Popover.Confirm>
	);
}

const EngagementSettingsFormToolbar = styled(Box)`
	position: fixed;
	bottom: 0;
	padding: 20px 0;
	background-color: ${(props) => props.theme.colors.white};
	width: 100%;
`;

const Header = () => {
	const [{ value }, , { setValue }] = useField<graphql.LocalizedString>('title');
	const [localizedValue, setLocalizedValue] = useLocalizedValue(value, setValue, '');

	if (localizedValue) {
		return (
			<Editable.Flex
				flexible
				value={localizedValue}
				onChange={(value) => {
					setLocalizedValue(value);
				}}
			>
				{(value) => <Title>{value || '&nbsp;'}</Title>}
			</Editable.Flex>
		);
	} else {
		return null;
	}
};

function EngagementSettingsForm(props: {
	template?: graphql.EngagementTemplate;
	defaults: graphql.EngagementIntervalDefault;
	userCount?: number;
	categories?: graphql.EngagementCategory[];
	toolbar?: ReactNode;
	requests?: graphql.EngagementRequest[];
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const hasEngagementReminder = useFeature(graphql.FeatureName.EngagementReminder);
	const hasOrderedEngagementSurveys = useFeature(graphql.FeatureName.OrderedEngagementSurveys);
	const hasEngagementEndPage = useFeature(graphql.FeatureName.EngagementEndPage);

	const [disable, { loading: disableLoading }] = graphql.useDisableEngagementTemplateMutation();
	const [enable, { loading: enableLoading }] = graphql.useEnableEngagementTemplateMutation();
	const [send, { loading: sendLoading }] = graphql.useSendEngagementTemplateMutation();
	const [sendReminders, { loading: sendRemindersLoading, error: sendRemindersError }] =
		graphql.useSendEngagementRequestRemindersMutation();
	const [cancelRequest, { loading: cancelRequestLoading, error: cancelRequestError }] =
		graphql.useCancelEngagementRequestMutation();
	const [loadingRequestId, setLoadingRequestId] = useState<string | null>(null);
	const [cancellingRequestId, setCancellingRequestId] = useState<string | null>(null);

	const title = props.template?.title ? localize(props.template?.title).text : '';

	const header = props.categories ? <Header /> : <Title.Skeleton />;

	// https://github.com/jaredpalmer/formik/issues/2338
	const formikContext = useContext(FormikContext);
	const useAllQuestions: boolean =
		!!formikContext && formikContext.values.useAllQuestions && hasOrderedEngagementSurveys;
	const isDirty = formikContext?.dirty;

	return (
		<>
			<Jumbo
				title=" "
				alternativePageTitle={title}
				description={t('settings.engagement.description')}
				header={header}
				footer={<Tabs.Container />}
				button={
					isDirty ? (
						<Button
							loading={formikContext?.isSubmitting}
							title={t('settings.engagement.toolbar.save')}
							onClick={() => formikContext?.submitForm()}
							disabled={!formikContext?.isValid}
						/>
					) : (
						<Flex>
							<Box mr={2}>
								<EngagementSettingsStatusButton
									type="secondary"
									loading={sendLoading}
									title={t('settings.engagement.setup.sendOnce')}
									confirmTitle={t('settings.engagement.setup.sendOnceConfirm', {
										count: props.userCount ?? 0,
									})}
									extraInfo={t('settings.engagement.setup.confirm.timing')}
									onConfirm={() => send({ variables: { input: { id: props.template!.id } } })}
								/>
							</Box>

							{props.categories && props.template ? (
								props.template.isEnabled ? (
									<EngagementSettingsStatusButton
										key="disable"
										loading={disableLoading}
										title={t('settings.engagement.setup.disable')}
										confirmTitle={t('settings.engagement.setup.disableConfirm', {
											count: props.userCount ?? 0,
										})}
										onConfirm={() => disable({ variables: { input: { id: props.template!.id } } })}
									/>
								) : (
									<EngagementSettingsStatusButton
										key="enable"
										loading={enableLoading}
										title={t('settings.engagement.setup.enable')}
										confirmTitle={t('settings.engagement.setup.enableConfirm', {
											count: props.userCount ?? 0,
										})}
										extraInfo={t('settings.engagement.setup.confirm.timing')}
										onConfirm={() => enable({ variables: { input: { id: props.template!.id } } })}
									/>
								)
							) : (
								<Button.Skeleton />
							)}
						</Flex>
					)
				}
			/>
			<Container pt={`${tokens.gap.page}px`} pb={4}>
				<FormCard
					title={t('settings.engagement.setup.recipients.title')}
					description={t('settings.engagement.setup.recipients.description')}
				>
					{props.categories ? (
						<GroupSelector.Formik
							canSelect={() => true}
							types={[graphql.GroupType.Department]}
							name="targetGroupIDs"
						/>
					) : (
						<GroupSelector.Skeleton />
					)}
				</FormCard>
				<FormCard
					title={t('settings.engagement.topics.title')}
					description={t('settings.engagement.topics.description')}
				>
					{props.categories ? <Topics categories={props.categories} /> : <MultiSelect.Skeleton />}
					{props.template && hasOrderedEngagementSurveys && <UseAllQuestions />}
				</FormCard>
				<FormCard
					title={t('settings.engagement.interval.title')}
					description={t(
						`settings.engagement.interval.description${hasOrderedEngagementSurveys ? (useAllQuestions ? 'CompleteSelection' : 'RandomSelection') : ''}`,
					)}
				>
					<Flex mt={3} alignItems="center">
						{props.categories ? (
							<IntervalSelector />
						) : (
							<Tags.Item.Skeleton label={t('settings.engagement.interval.every.label')} />
						)}
						{props.template && !useAllQuestions && <MaxQuestions defaults={props.defaults} />}
					</Flex>
					{props.template && <Expiry />}
				</FormCard>
				{hasEngagementReminder && (
					<FormCard
						title={t('settings.engagement.reminder.title')}
						description={t('settings.engagement.reminder.description')}
					>
						{props.categories ? <Reminder /> : <Checkbox.Skeleton />}
					</FormCard>
				)}
				<FormCard
					title={t('settings.engagement.enps.title')}
					description={t('settings.engagement.enps.description')}
				>
					{props.categories ? (
						<Flex>
							<Checkbox.Formik name="useNps" />
							<Label ml={2}>{t('settings.engagement.enps.label')}</Label>
						</Flex>
					) : (
						<Checkbox.Skeleton />
					)}
				</FormCard>
				<FormCard
					title={t('settings.engagement.anonymityThreshold.title')}
					description={t('settings.engagement.anonymityThreshold.description')}
				>
					{props.categories ? (
						<Flex>
							<AnonymityThreshold hasSentSurvey={props.requests && props.requests.length > 0} />
						</Flex>
					) : (
						<Tags.Item.Skeleton label={t('settings.engagement.anonymityThreshold.label')} />
					)}
				</FormCard>
				<FormCard
					title={t('settings.engagement.instruction.title')}
					description={t('settings.engagement.instruction.description')}
				>
					{props.categories ? (
						<QuillInput.Localizable.Formik name="instruction" />
					) : (
						<Skeleton.Box height={173} />
					)}
				</FormCard>
				{hasEngagementEndPage && (
					<FormCard
						title={t('settings.engagement.endPage.title')}
						description={t('settings.engagement.endPage.description')}
					>
						{props.categories ? (
							<QuillInput.Localizable.Formik name="endPage" />
						) : (
							<Skeleton.Box height={173} />
						)}
					</FormCard>
				)}
				<Box mb={5}>
					<SettingsSection title={t('settings.engagement.result.title')}>
						{props.categories ? (
							<EngagementSettingsSummary />
						) : (
							<Paragraph mb={2}>
								{t('settings.engagement.result.description.placeholder')}{' '}
							</Paragraph>
						)}
						{props.requests && (
							<>
								<Paragraph mb={2}>{t('settings.engagement.results.description')}</Paragraph>
								<Table>
									<Table.Header>
										<Table.Cell basis={1}>
											{t('settings.engagement.results.header.date')}
										</Table.Cell>
										<Table.Cell align="right" basis={0.2}>
											{t('settings.engagement.results.header.questions')}
										</Table.Cell>
										<Table.Cell align="right" basis={0.2}>
											{t('settings.engagement.results.header.sent')}
										</Table.Cell>
										<Table.Cell align="right" basis={0.2}>
											{t('settings.engagement.results.header.completed')}
										</Table.Cell>
										<Table.Cell align="right" basis={0.2}>
											{t('settings.engagement.results.header.percentage')}
										</Table.Cell>
									</Table.Header>
									{props.requests.map((request) => {
										const isExpired = !moment(request.createdAt)
											.add(props.template?.expiryInDays ?? 7, 'days')
											.endOf('day')
											.isAfter(moment());
										const isActive = !request.isCancelled && !isExpired;

										return (
											<Table.Row key={request.id}>
												<Table.Cell basis={1}>
													{moment(request.createdAt).format('LLL')}
													{request.isCancelled ? (
														<Button
															type="transparent"
															title={t('settings.engagement.results.cta.cancelled')}
															small
															disabled
															ml={3}
														/>
													) : isActive ? (
														<>
															<Button
																type="transparent"
																title={t('settings.engagement.results.cta.sendReminders')}
																small
																onClick={async () => {
																	setLoadingRequestId(request.id);
																	try {
																		await sendReminders({
																			variables: { input: { id: request.id } },
																		});
																	} finally {
																		setLoadingRequestId(null);
																	}
																}}
																loading={loadingRequestId === request.id && sendRemindersLoading}
																disabled={
																	loadingRequestId !== null && loadingRequestId !== request.id
																}
																ml={3}
															/>
															<Popover.Confirm
																visible={cancellingRequestId === request.id}
																title={t('settings.engagement.results.cta.cancelConfirm')}
																confirmTitle={t('settings.engagement.setup.confirm.yes')}
																cancelTitle={t('settings.engagement.setup.confirm.no')}
																onCancel={() => setCancellingRequestId(null)}
																onConfirm={async () => {
																	setCancellingRequestId(null);
																	setLoadingRequestId(request.id);
																	try {
																		await cancelRequest({
																			variables: { input: { id: request.id } },
																		});
																	} finally {
																		setLoadingRequestId(null);
																	}
																}}
															>
																<Button
																	type="transparent"
																	title={t('settings.engagement.results.cta.cancel')}
																	small
																	onClick={() => setCancellingRequestId(request.id)}
																	loading={loadingRequestId === request.id && cancelRequestLoading}
																	disabled={
																		loadingRequestId !== null && loadingRequestId !== request.id
																	}
																	ml={2}
																/>
															</Popover.Confirm>
														</>
													) : (
														<Button
															type="transparent"
															title={t('settings.engagement.results.cta.expired')}
															small
															disabled
															ml={3}
														/>
													)}
												</Table.Cell>
												<Table.Cell align="right" basis={0.2}>
													{request.questions.length}
												</Table.Cell>
												<Table.Cell align="right" basis={0.2}>
													{request.sent}
												</Table.Cell>
												<Table.Cell align="right" basis={0.2}>
													{request.completed}
												</Table.Cell>
												<Table.Cell align="right" basis={0.2}>
													{Math.round(request.progress * 100)}%
												</Table.Cell>
											</Table.Row>
										);
									})}
								</Table>
								{(sendRemindersError || cancelRequestError) && (
									<GraphQLErrorMessage error={sendRemindersError || cancelRequestError} />
								)}
							</>
						)}
					</SettingsSection>
				</Box>
				{props.toolbar && (
					<EngagementSettingsFormToolbar mt={4}>{props.toolbar}</EngagementSettingsFormToolbar>
				)}
			</Container>
		</>
	);
}

function EngagementSettings(props: {
	template: graphql.EngagementTemplate;
	categories: graphql.EngagementCategory[];
	defaults: graphql.EngagementIntervalDefault[];
	requests: graphql.EngagementRequest[];
}) {
	const { t } = useTranslation();

	const [update, { loading }] = graphql.useUpdateEngagementTemplateMutation();

	const formik = useFormik({
		initialValues: {
			title: props.template.title,
			interval: props.template.interval,
			categories: props.template.categories,
			useAllQuestions: props.template.useAllQuestions,
			maxQuestions: props.template.maxQuestions?.toFixed(),
			maxOpenQuestions: props.template.maxOpenQuestions?.toFixed(),
			expiry: props.template.expiryInDays?.toFixed(),
			targetGroupIDs: props.template.targetGroups.map(({ id }) => id),
			useNps: props.template.useNps ?? false,
			instruction: props.template.instruction ?? [],
			endPage: props.template.endPage ?? [],
			reminderDaysInAdvance: props.template.reminderDaysInAdvance?.toFixed(),
			anonymityThreshold: (props.template.anonymityThreshold ?? 5).toString(),
		},
		validationSchema: Yup.object({
			expiry: Yup.number()
				.nullable()
				.default(7)
				.min(1, ({ min: minValue }) =>
					t('settings.engagement.configuration.expiry.error.min', { minValue }),
				),
			reminderDaysInAdvance: Yup.number()
				.nullable()
				.default(REMINDER_MIN_IN_DAYS)
				.min(REMINDER_MIN_IN_DAYS, ({ min: minValue }) =>
					t('settings.engagement.configuration.reminder.error.min', { minValue }),
				)
				.test({
					name: 'max',
					exclusive: false,
					params: {},
					test: function (value) {
						if (value) {
							const expiryInDays = this.parent.expiry ?? 7;

							if (value >= expiryInDays) {
								return this.createError({
									message: t('settings.engagement.configuration.reminder.error.max', {
										maxValue: expiryInDays - 1,
									}),
								});
							}

							return true;
						}
					},
				}),
		}),
		onSubmit: async (values, { resetForm }) => {
			await update({
				variables: {
					input: {
						id: props.template.id,
						title: values.title,
						interval: values.interval,
						categoryIDs: values.categories?.map(({ id }) => id) ?? [],
						useAllQuestions: values.useAllQuestions,
						maxQuestions:
							values.maxQuestions === undefined ? undefined : parseInt(values.maxQuestions),
						maxOpenQuestions:
							values.maxOpenQuestions === undefined ? undefined : parseInt(values.maxOpenQuestions),
						expiryInDays: values.expiry === undefined ? undefined : parseInt(values.expiry),
						targetGroupIDs: values.targetGroupIDs,
						useNps: values.useNps,
						instruction: values.instruction,
						endPage: values.endPage,
						reminderDaysInAdvance:
							values.reminderDaysInAdvance === undefined
								? undefined
								: parseInt(values.reminderDaysInAdvance),
						anonymityThreshold: parseInt(values.anonymityThreshold),
					},
				},
			});
			resetForm({ values });
		},
	});

	const defaults = props.defaults.find((defaults) => defaults.interval === formik.values.interval);

	return (
		<FormikProvider value={formik}>
			<EngagementSettingsForm
				template={props.template}
				categories={props.categories}
				userCount={props.template.targetUsers.length}
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				defaults={defaults}
				requests={props.requests}
				toolbar={
					<Toolbar>
						<Button
							loading={loading}
							title={t('settings.engagement.toolbar.save')}
							onClick={() => formik.submitForm()}
							disabled={!formik.isValid}
						/>
					</Toolbar>
				}
			/>
		</FormikProvider>
	);
}

export default function EngagementSettingsWithoutData(props: { id: string }) {
	const query = graphql.useEngagementTemplateQuery({ variables: { id: props.id } });

	if (!query.data) {
		return (
			<EngagementSettingsForm
				defaults={{
					interval: graphql.EngagementInterval.Yearly,
					maxQuestions: 1,
					maxOpenQuestions: 1,
				}}
			/>
		);
	}

	const categories = query.data.builtinEngagementCategories.edges
		.concat(query.data.engagementCategories.edges)
		.map(({ node }) => node as graphql.EngagementCategory);

	const requests = query.data.engagementRequests.edges
		.map(({ node }) => node as graphql.EngagementRequest)
		.reverse();

	return (
		<EngagementSettings
			key={props.id}
			template={query.data.engagementTemplate as graphql.EngagementTemplate}
			categories={categories}
			defaults={query.data.engagementIntervalDefaults}
			requests={requests}
		/>
	);
}
